import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { applyAccentColor, cacheAccentColor } from "@/lib/branding";
import { setThemeChoice } from "@/lib/theme";
import { useAccessibility } from "@/contexts/AccessibilityContext";

const AuthContext = createContext(null);


async function syncBranding() {
  try {
    const { data } = await api.get("/branding");
    applyAccentColor(data.accent_color);
    cacheAccentColor(data.accent_color);
  } catch {
    applyAccentColor(null);
    cacheAccentColor(null);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const { applyAccount } = useAccessibility();

  const signedIn = useCallback((data) => {
    if (data.theme) setThemeChoice(data.theme);
    applyAccount(data);
    syncBranding();
  }, [applyAccount]);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (data.authenticated) {
        setUser(data);
        signedIn(data);
      } else {
        setUser(false);
        applyAccentColor(null);
      }
    } catch {
      setUser(false);
      applyAccentColor(null);
    }
  }, [signedIn]);

  useEffect(() => {
    const { hash, pathname } = window.location;
    if (hash.includes("session_id=") || pathname === "/auth/complete") return;
    refreshUser();
  }, [refreshUser]);

  const socialLogin = useCallback(async (path, body) => {
    try {
      const { data } = await api.post(path, body);
      setUser(data);
      signedIn(data);
      return { success: true };
    } catch (e) {
      setUser(false);
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, [signedIn]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      signedIn(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      setUser(data);
      signedIn(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(false);
      applyAccentColor(null);
      cacheAccentColor(null);
      setThemeChoice(null);
    }
  };

  const setTheme = async (theme) => {
    setThemeChoice(theme);
    setUser((u) => (u ? { ...u, theme } : u));
    try {
      await api.put("/auth/preferences", { theme });
    } catch {
      // keep the local choice even if saving fails
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, socialLogin, refreshUser, setTheme }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
